/**
 * Error boundary for isolating WebGL and other failures
 */

export class ErrorBoundary {
  #container;
  #errorHandler;
  #isError = false;

  constructor(containerId = 'errorBoundary') {
    this.#container = document.getElementById(containerId);
    this.#errorHandler = this.#handleError.bind(this);
    
    this.#setupListeners();
  }

  #setupListeners() {
    // Global error handler
    window.addEventListener('error', this.#errorHandler);
    window.addEventListener('unhandledrejection', this.#handleRejection);
    
    // WebGL context loss
    document.addEventListener('webglcontextlost', this.#handleWebGLContextLost);
  }

  #handleError = (event) => {
    console.error('[ErrorBoundary] Caught error:', event.error);
    this.show();
    return false;
  };

  #handleRejection = (event) => {
    console.error('[ErrorBoundary] Unhandled rejection:', event.reason);
    // Only show for critical errors
    if (this.#isCriticalError(event.reason)) {
      this.show();
    }
  };

  #handleWebGLContextLost = (event) => {
    console.warn('[ErrorBoundary] WebGL context lost');
    event.preventDefault();
    this.show();
  };

  #isCriticalError(reason) {
    if (!reason) return false;
    const errorString = String(reason);
    return (
      errorString.includes('WebGL') ||
      errorString.includes('Three.js') ||
      errorString.includes('context') ||
      errorString.includes('out of memory')
    );
  }

  show() {
    if (this.#isError) return;
    this.#isError = true;
    
    if (this.#container) {
      this.#container.classList.add('active');
      this.#container.setAttribute('aria-hidden', 'false');
    }
    
    // Pause all animations
    document.body.classList.add('error-state');
    
    // Dispatch custom event for other modules
    window.dispatchEvent(new CustomEvent('app:error', {
      detail: { timestamp: Date.now() }
    }));
  }

  hide() {
    this.#isError = false;
    
    if (this.#container) {
      this.#container.classList.remove('active');
      this.#container.setAttribute('aria-hidden', 'true');
    }
    
    document.body.classList.remove('error-state');
  }

  wrap(fn, context = 'unknown') {
    return (...args) => {
      try {
        return fn.apply(this, args);
      } catch (error) {
        console.error(`[ErrorBoundary] Error in ${context}:`, error);
        this.show();
        return null;
      }
    };
  }

  async wrapAsync(fn, context = 'unknown') {
    try {
      return await fn();
    } catch (error) {
      console.error(`[ErrorBoundary] Error in ${context}:`, error);
      this.show();
      return null;
    }
  }

  destroy() {
    window.removeEventListener('error', this.#errorHandler);
    window.removeEventListener('unhandledrejection', this.#handleRejection);
    document.removeEventListener('webglcontextlost', this.#handleWebGLContextLost);
  }
}

// Singleton instance
let instance = null;

export function initErrorBoundary() {
  if (!instance) {
    instance = new ErrorBoundary();
  }
  return instance;
}

export function getErrorBoundary() {
  return instance;
}
